import { Divider, Radio, RadioChangeEvent } from 'antd';
import { RadioGroupOptionType } from 'antd/lib/radio';
import classNames from 'classnames';
import { ChangeEvent, FC, KeyboardEvent, ReactNode, memo, useState } from 'react';
import s from './index.module.scss';

export enum RadioOptionType {
  OPTION = 'option',
  DIVIDER = 'divider',
}

export enum RadioOptionTheme {
  DEFAULT = 'default',
  WARNING = 'warning',
  CLEAR = 'clear',
}

export interface ModernRadioOption {
  value: string;
  label: ReactNode;
  type?: RadioOptionType;
  theme?: RadioOptionTheme;
}

interface ModernRadioButtonGroupProps {
  options: ModernRadioOption[];
  value?: string;
  direction?: 'horizontal' | 'vertical';
  withBorder?: boolean;
  onChange: (value: CustomAny) => void;
  buttonClassName?: string;
  labelPosition?: 'prev' | 'post';
  isDisabled?: boolean;
  optionType?: RadioGroupOptionType;
  onAddOption?: (label: string) => void;
}

export const ModernRadioButtonGroup: FC<ModernRadioButtonGroupProps> = memo((props) => {
  const {
    options,
    value,
    direction = 'horizontal',
    withBorder = false,
    onChange,
    buttonClassName,
    labelPosition = 'post',
    isDisabled = false,
    optionType,
    onAddOption,
  } = props;

  const [newOptionMode, setNewOptionMode] = useState<'view' | 'edit'>('view');
  const [newOptionLabel, setNewOptionLabel] = useState<string | undefined>();

  const addNewOptionKey = 'addNewOption';

  const newOptionLabelChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setNewOptionLabel(e.target.value);
  };

  const closeEditOptionMode = (): void => {
    setNewOptionMode('view');
    setNewOptionLabel(undefined);
  };

  const applyNewOptionLabel = (): void => {
    if (newOptionLabel) {
      onAddOption?.(newOptionLabel);
      closeEditOptionMode();
    } else {
      closeEditOptionMode();
    }
  };

  const onEnterKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      applyNewOptionLabel();
    }

    if (e.key === 'Escape') {
      closeEditOptionMode();
    }
  };

  const handleChange = (e: RadioChangeEvent): void => {
    e.target.value === addNewOptionKey ? setNewOptionMode('edit') : onChange?.(e.target.value);
  };

  const directionClass: Record<'horizontal' | 'vertical', string> = {
    horizontal: classNames('flex items-center flex-nowrap gap-1.5 desktop:gap-4', {
      'gap-2.5': optionType === 'button',
    }),
    vertical: classNames('flex flex-col gap-4'),
  };

  const getLabelClass = (isActive: boolean): string => {
    return classNames({ 'font-normal': !isActive });
  };

  const getRadioButtonClass = (isActive: boolean, isAddNewButton: boolean, theme?: RadioOptionTheme): string => {
    return classNames(
      'cursor-pointer ease-linear duration-200 w-full',
      {
        border: withBorder && !isAddNewButton,
        'border-black': isActive && withBorder,
        'border-backgroundColor': !isActive && withBorder,
        'p-2 rounded-lg': optionType === 'default',
        'border-l-2 border-l-secondaryAccent': isAddNewButton,
        'bg-accentLight': theme === RadioOptionTheme.DEFAULT || !theme,
        'bg-warn': theme === RadioOptionTheme.WARNING,
        '!border-warn': theme === RadioOptionTheme.WARNING && !isActive && withBorder,
        'bg-transparent': theme === RadioOptionTheme.CLEAR,
      },
      buttonClassName,
    );
  };

  const resultOptions = onAddOption ? [...options, { value: addNewOptionKey, label: '+', theme: RadioOptionTheme.DEFAULT }] : options;

  return (
    <Radio.Group
      optionType={optionType}
      buttonStyle="solid"
      className={directionClass[direction]}
      onChange={handleChange}
      disabled={isDisabled}
      value={value}
    >
      {resultOptions.map((option) => {
        if (option.type === 'divider') {
          return (
            <div key={`divider-${String(option.label)}`} className="w-full">
              <Divider className="my-1" />
              <div className="font-semibold text-base">{option.label}</div>
            </div>
          );
        }

        const isActive = option.value === value;
        const isAddNewButton = option.value === addNewOptionKey;

        return (
          <label key={option.value} className={getRadioButtonClass(isActive, isAddNewButton, option.theme)}>
            <Radio
              value={option.value}
              className={classNames(s.radio, 'w-full flex items-center', {
                'flex-row-reverse': labelPosition === 'prev',
              })}
              data-theme={option.theme}
            >
              {isAddNewButton && newOptionMode === 'edit' ? (
                <input
                  type="text"
                  value={newOptionLabel}
                  autoFocus
                  onKeyDown={onEnterKeyPress}
                  onBlur={applyNewOptionLabel}
                  onChange={newOptionLabelChange}
                />
              ) : (
                <div className={getLabelClass(isActive)}>{option.label}</div>
              )}
            </Radio>
          </label>
        );
      })}
    </Radio.Group>
  );
});
